import {
  FlaskConical,
  LayoutDashboard,
  MessageCircle,
  Package,
  Rocket,
  UserRound,
} from 'lucide-react'

export const TOPICS = [
  {
    id: 'start',
    title: 'Getting Started',
    description: 'First login, password reset, and what to do next.',
    icon: Rocket,
    articles: [
      {
        id: 'first-login',
        title: 'How first login works',
        summary: 'Use the username and temporary password you received, then create a private password.',
        steps: [
          'Your patient portal account is created for you.',
          'You receive a username and temporary password.',
          'Sign in with the temporary password.',
          'Create a new private password when prompted.',
          'After that, use the portal tabs for labs, orders, visits, messages, prescriptions, and account details.',
        ],
      },
      {
        id: 'what-next',
        title: 'What should I do first?',
        summary: 'Start with Dashboard and Notifications, then handle labs, forms, or messages.',
        steps: [
          'Check Dashboard and Notifications for anything waiting on you.',
          'Upload recent labs in Labs if you already have them.',
          'Go to Shop to purchase labs if you do not have recent results.',
          'Complete any forms or signatures listed in Account.',
          'Use Messages if you are unsure what applies to you.',
        ],
      },
      {
        id: 'walkthrough',
        title: 'Replay the portal walkthrough',
        summary: 'Start the guided tour again any time.',
        action: 'tour',
      },
    ],
  },
  {
    id: 'tabs',
    title: 'Portal Tabs',
    description: 'What Dashboard, Shop, Visits, Messages, Labs, Orders, and Account are for.',
    icon: LayoutDashboard,
    articles: [
      {
        id: 'dashboard',
        title: 'Dashboard and Notifications',
        summary: 'Dashboard is your home base. Notifications collect unread messages, lab updates, prescription updates, orders, and visit reminders.',
        links: [{ label: 'Open Dashboard', to: '/dashboard' }],
      },
      {
        id: 'shop',
        title: 'Shop',
        summary: 'Shop is where you purchase labs, request refills, or start a treatment request. Some requests may need labs, forms, payment, care review, or a visit.',
        links: [{ label: 'Open Shop', to: '/shop' }],
      },
      {
        id: 'tabs-overview',
        title: 'Visits, Messages, Prescriptions, Labs, Orders, and Account',
        summary: 'Visits shows appointments. Messages reaches the right team. Prescriptions shows medication requests and refills. Labs holds uploads and results. Orders tracks purchases and fulfillment. Account holds profile, forms, payments, and documents.',
      },
    ],
  },
  {
    id: 'labs',
    title: 'Labs and Testing',
    description: 'Recent labs, BioBox, mobile phlebotomy, and requisitions.',
    icon: FlaskConical,
    articles: [
      {
        id: 'upload-labs',
        title: 'How to upload recent labs',
        summary: 'Upload the original PDF or image in Labs with your name and collection date visible.',
        steps: [
          'Go to Labs.',
          'Choose the upload option for recent results.',
          'Upload the PDF or image exactly as provided by the lab.',
          'Make sure your name and collection date are visible.',
          'Message Support Team if the file will not upload.',
        ],
        links: [{ label: 'Open Labs', to: '/labs' }],
      },
      {
        id: 'biobox',
        title: 'How BioBox works',
        summary: 'BioBox is an at-home lab kit. Follow the kit instructions exactly and return the sample promptly.',
        steps: [
          'Purchase the BioBox option if it is offered for your lab panel.',
          'Watch Orders for shipping updates.',
          'Read every instruction card before collecting your sample.',
          'Collect, label, package, and return the sample exactly as instructed.',
          'Message Support Team if anything is missing, damaged, expired, or confusing.',
        ],
      },
      {
        id: 'mobile-phlebotomy',
        title: 'Mobile phlebotomy',
        summary: 'If mobile collection is available, follow the scheduling instructions and have your ID and paperwork ready if requested.',
        steps: [
          'Choose a draw date, time, and address when prompted.',
          'Follow fasting, hydration, medication, or timing instructions shown in your lab order.',
          'Have your ID and lab paperwork available if requested.',
          'After collection, the sample is sent to the lab and results appear after processing.',
        ],
      },
      {
        id: 'requisitions',
        title: 'Requisition forms',
        summary: 'A requisition is the lab order paperwork. Bring it to the lab if the instructions say to do so.',
        steps: [
          'Open or download the requisition from Labs or Orders when available.',
          'Confirm your name, date of birth, and ordered panel look correct.',
          'Bring it to the lab if instructed.',
          'If the lab says it is missing or invalid, message Support Team before leaving if possible.',
        ],
      },
    ],
  },
  {
    id: 'support',
    title: 'Messages and Support',
    description: 'Who to contact and what to include.',
    icon: MessageCircle,
    articles: [
      {
        id: 'provider-vs-support',
        title: 'Clinical questions vs Support Team',
        summary: 'Clinical questions are about symptoms, medications, side effects, lab interpretation, or treatment decisions. Support Team helps with portal use, orders, shipping, billing, forms, uploads, BioBox, scheduling, and requisitions.',
      },
      {
        id: 'message-details',
        title: 'What to include in a message',
        summary: 'Say what you are trying to do, include the order/lab/visit/prescription/form involved, and attach screenshots or files when helpful.',
      },
      {
        id: 'support-widget',
        title: 'Use the Support Widget',
        summary: 'The floating Support button is the quickest place to request a product in the portal, get help, report a bug, or request a feature.',
        steps: [
          'Click the floating Support button in the bottom-right corner of the portal.',
          'Choose Need Help for orders, labs, billing, uploads, forms, account access, or portal questions.',
          'Choose Request Product when there is a product you would like KIS Rx to review for the portal.',
          'Choose Report Bug when something looks broken or does not work as expected.',
          'Choose Request Feature when you have an idea that would improve the portal.',
        ],
        action: 'support-widget',
      },
      {
        id: 'emergency',
        title: 'This portal is not for emergencies',
        summary: 'If you may be having a medical emergency or severe reaction, call 911 or seek emergency care. Portal messages are not monitored as an emergency channel.',
      },
    ],
  },
  {
    id: 'orders',
    title: 'Orders and Prescriptions',
    description: 'Status, refills, care review, and shipping.',
    icon: Package,
    articles: [
      {
        id: 'order-status',
        title: 'Understanding order status',
        summary: 'Orders can move through payment, questions, care review, lab requirement, prescription approval, fulfillment, shipping, or completion.',
        links: [{ label: 'Open Orders', to: '/orders' }],
      },
      {
        id: 'provider-review',
        title: 'What care review means',
        summary: 'A provider is reviewing your request. They may need labs, forms, a visit, or more details before approving, changing, or declining it.',
      },
      {
        id: 'refills',
        title: 'How refills work',
        summary: 'Go to Prescriptions or Shop, choose the refill path when available, confirm details, complete questions, and watch Orders for fulfillment updates.',
        links: [{ label: 'Open Prescriptions', to: '/prescriptions' }],
      },
    ],
  },
  {
    id: 'account',
    title: 'Account and Billing',
    description: 'Profile, forms, documents, payments, credits, and privacy.',
    icon: UserRound,
    articles: [
      {
        id: 'profile',
        title: 'Why profile details matter',
        summary: 'Your name, date of birth, phone, state, address, pharmacy, and shipping information can affect lab orders, prescriptions, shipping, and care review.',
        links: [{ label: 'Open Account', to: '/account' }],
      },
      {
        id: 'forms',
        title: 'Forms and documents',
        summary: 'Forms may include intake questions, consent forms, signatures, provider-requested paperwork, or order-specific questionnaires.',
        links: [{ label: 'Open Forms', to: '/account#forms' }],
      },
      {
        id: 'payments',
        title: 'Payment methods and credits',
        summary: 'Payment information is used for eligible purchases, subscriptions, prescriptions, or refill billing. Credits can apply to eligible orders, labs, or visits.',
      },
    ],
  },
]
